import type { GetEstimatedCost } from '@repo/schema/bill'
import type { CopyLoadDetailsFieldKey } from '@repo/schema/page-data'
import { formatCurrency, formatWeight } from '@repo/sdk/helpers'

export type CopyLoadDetailsInput = {
  carrier_rate?: GetEstimatedCost | null
  carrierRate?: GetEstimatedCost | null
  drops?: null | readonly Stop[]
  financials?: null | readonly Financial[]
  goods?: Text
  goods_description?: Text
  load_type?: null | { name?: Text; title?: Text }
  packaging?: Text
  packaging_type?: null | { title?: Text }
  pallets?: null | number
  required_vehicle_type?: Text
  requirements?: null | readonly Requirement[] | Requirements
  route?: null | { stops?: null | readonly Stop[] }
  temperature?: null | number
  vehicle?: Text | Vehicle
  vehicle_type?: Text
  weight?: null | number
  weight_unit?: 'kg' | 't' | null
}

export type CopyLoadDetailsOptions = {
  canCopyHaulierRate?: boolean
  canCopyShipperRate?: boolean
  carrierRate?: GetEstimatedCost | null
}

export type CopyLoadDetailsSettings = {
  outputStyle: 'compact' | 'detailed'
  selectedFields: readonly CopyLoadDetailsFieldKey[]
}

type Address = {
  address?: Text
  address_second_line?: Text
  city?: Text
  country?: Text
  country_code?: Text
  postcode?: Text
}

type Financial = {
  alias?: Text
  amount?: null | number
  currency?: Text | { alias?: Text }
  type?: null | { alias?: Text }
}

type Requirement = string | { name?: Text; title?: Text }

type Requirements = {
  adr?: boolean | null
  crane?: boolean | null
  dvs?: boolean | null
  tail_lift?: boolean | null
  waste_license?: boolean | null
}

type Stop = {
  address?: Text
  date?: Date | null | string
  full_address?: Text
  location?: null | StopLocation
  location_name?: Text
  name?: Text
  order?: null | number
  place?: null | StopPlace
  requested_date?: Date | null | string
  requested_time?: Text
  time?: null | StopTime
  time_window?: Text
  type?: Text
}

type StopLocation = {
  address?: Address | null | Text
  name?: Text
}

type StopPlace = {
  address?: Text
  city?: Text
  country?: Text
  country_code?: Text
  formattedAddress?: Text
  name?: Text
  place_name?: Text
  postcode?: Text
}

type StopTime = {
  from?: Text
  to?: Text
  type?: '24' | 'asap' | 'at' | 'between' | 'by' | 'tbc' | null
}

type Text = null | string

type Vehicle = {
  name?: Text
  title?: Text
  type?: null | { name?: Text; title?: Text }
}

const routeFieldKeys: readonly CopyLoadDetailsFieldKey[] = [
  'routeStopType',
  'routeLocationName',
  'routeAddress',
  'routeDate',
  'routeTime',
]

const clean = (value: Text | undefined): null | string => {
  const trimmed = value?.trim()
  return trimmed || null
}

const joinValues = (values: readonly (null | string)[], separator: string): null | string => {
  const joined = values.filter((value): value is string => value !== null).join(separator)
  return joined || null
}

const has = (settings: CopyLoadDetailsSettings, key: CopyLoadDetailsFieldKey) =>
  settings.selectedFields.includes(key)

const labelled = (label: string, value: null | string): null | string =>
  value ? `${label}: ${value}` : null

const formatDate = (value: Date | null | Text | undefined): null | string => {
  if (value instanceof Date) {
    return Number.isNaN(value.valueOf()) ? null : value.toISOString().slice(0, 10)
  }

  return clean(value)
}

const formatAddress = (address: Address | null | Text | undefined): null | string => {
  if (typeof address === 'string') return clean(address)
  if (!address) return null

  const locality = joinValues(
    [
      clean(address.postcode),
      clean(address.city),
      clean(address.country_code) ?? clean(address.country),
    ],
    ', ',
  )

  return joinValues([clean(address.address), clean(address.address_second_line), locality], ', ')
}

const formatStopAddress = (stop: Stop): null | string =>
  formatAddress(stop.location?.address) ??
  clean(stop.place?.formattedAddress) ??
  formatAddress({
    address: stop.place?.address,
    city: stop.place?.city,
    country: stop.place?.country,
    country_code: stop.place?.country_code,
    postcode: stop.place?.postcode,
  }) ??
  clean(stop.full_address) ??
  clean(stop.address)

const formatStopTime = (time: null | StopTime | undefined): null | string => {
  if (!time) return null

  const from = clean(time.from)
  const to = clean(time.to)

  switch (time.type) {
    case '24':
      return 'Whole day'
    case 'asap':
      return 'ASAP'
    case 'at':
      return from ? `At ${from}` : null
    case 'between':
      return from && to ? `${from}–${to}` : (from ?? to)
    case 'by':
      return to ? `By ${to}` : null
    case 'tbc':
      return 'TBC'
    default:
      return from && to ? `${from}–${to}` : (from ?? to)
  }
}

const formatStopType = (type: Text | undefined): string => {
  switch (type?.trim().toLowerCase()) {
    case 'collection':
      return 'Collection'
    case 'delivery':
      return 'Delivery'
    default:
      return 'Stop'
  }
}

const formatStop = (stop: Stop, settings: CopyLoadDetailsSettings): null | string => {
  const locationName =
    clean(stop.location?.name) ??
    clean(stop.place?.name) ??
    clean(stop.place?.place_name) ??
    clean(stop.location_name) ??
    clean(stop.name)
  const requestedDate = formatDate(stop.requested_date) ?? formatDate(stop.date)
  const requestedTime =
    clean(stop.requested_time) ?? clean(stop.time_window) ?? formatStopTime(stop.time)
  const dateAndTime = joinValues(
    [
      has(settings, 'routeDate') ? requestedDate : null,
      has(settings, 'routeTime') ? requestedTime : null,
    ],
    ' ',
  )

  const stopType = has(settings, 'routeStopType') ? formatStopType(stop.type) : null
  const stopDetails = joinValues(
    [
      has(settings, 'routeLocationName') ? locationName : null,
      has(settings, 'routeAddress') ? formatStopAddress(stop) : null,
      dateAndTime,
    ],
    ', ',
  )

  return stopType && stopDetails ? `${stopType} — ${stopDetails}` : (stopType ?? stopDetails)
}

const stopOrder = (stop: Stop): null | number =>
  typeof stop.order === 'number' && Number.isFinite(stop.order) ? stop.order : null

const getOrderedStops = (input: CopyLoadDetailsInput): readonly Stop[] => {
  const stops = input.drops ?? input.route?.stops ?? []

  return stops
    .map((stop, index) => ({ index, stop }))
    .sort((left, right) => {
      const leftOrder = stopOrder(left.stop)
      const rightOrder = stopOrder(right.stop)

      if (leftOrder === null && rightOrder === null) return left.index - right.index
      if (leftOrder === null) return 1
      if (rightOrder === null) return -1

      return leftOrder - rightOrder || left.index - right.index
    })
    .map(({ stop }) => stop)
}

const isRequirementList = (
  requirements: CopyLoadDetailsInput['requirements'],
): requirements is readonly Requirement[] => Array.isArray(requirements)

const formatRequirements = (requirements: CopyLoadDetailsInput['requirements']): null | string => {
  if (!requirements) return null

  if (isRequirementList(requirements)) {
    return joinValues(
      requirements.map(requirement =>
        typeof requirement === 'string'
          ? clean(requirement)
          : (clean(requirement.title) ?? clean(requirement.name)),
      ),
      ', ',
    )
  }

  return joinValues(
    [
      requirements.adr ? 'ADR' : null,
      requirements.dvs ? 'DVS' : null,
      requirements.waste_license ? 'Waste licence' : null,
      requirements.tail_lift ? 'Tail lift' : null,
      requirements.crane ? 'Crane' : null,
    ],
    ', ',
  )
}

const formatVehicle = (input: CopyLoadDetailsInput): null | string => {
  if (typeof input.vehicle === 'string') return clean(input.vehicle)

  return (
    clean(input.vehicle?.title) ??
    clean(input.vehicle?.name) ??
    clean(input.vehicle?.type?.title) ??
    clean(input.vehicle?.type?.name) ??
    clean(input.required_vehicle_type) ??
    clean(input.vehicle_type) ??
    clean(input.load_type?.title) ??
    clean(input.load_type?.name)
  )
}

const formatMoney = (
  value:
    | null
    | undefined
    | {
        amount?: null | number
        currency?: Text | { alias?: Text }
      },
): null | string => {
  if (value?.amount === null || value?.amount === undefined) return null
  if (!Number.isFinite(value.amount)) return null

  const currency =
    typeof value.currency === 'string' ? clean(value.currency) : clean(value.currency?.alias)

  return currency
    ? formatCurrency({ amount: value.amount, currency, showCurrencyCode: true })
    : null
}

const fullRate = (financials: CopyLoadDetailsInput['financials']): Financial | null =>
  financials?.find(financial => (financial.type?.alias ?? financial.alias) === 'rate') ?? null

const formatRoute = (
  input: CopyLoadDetailsInput,
  settings: CopyLoadDetailsSettings,
): null | string => {
  if (!routeFieldKeys.some(key => has(settings, key))) return null

  const stops = getOrderedStops(input)
    .map(stop => formatStop(stop, settings))
    .filter((stop): stop is string => stop !== null)

  if (stops.length === 0) return null

  if (settings.outputStyle === 'compact') {
    return labelled('Route', stops.join(' → '))
  }

  return `${stops.map(stop => `• ${stop}`).join('\n')}`
}

const formatCargo = (
  input: CopyLoadDetailsInput,
  settings: CopyLoadDetailsSettings,
): readonly string[] => {
  const values: string[] = []
  const add = (key: CopyLoadDetailsFieldKey, label: string, value: null | string) => {
    const field = has(settings, key) ? labelled(label, value) : null
    if (field) values.push(field)
  }

  add('cargoGoods', 'Goods', clean(input.goods_description) ?? clean(input.goods))
  add('cargoPackaging', 'Packaging', clean(input.packaging_type?.title) ?? clean(input.packaging))
  add(
    'cargoPallets',
    'Pallets',
    input.pallets === null || input.pallets === undefined ? null : String(input.pallets),
  )
  add(
    'cargoWeight',
    'Weight',
    input.weight === null || input.weight === undefined
      ? null
      : formatWeight(input.weight, input.weight_unit ?? undefined) || null,
  )
  add(
    'cargoTemperature',
    'Temperature',
    input.temperature === null || input.temperature === undefined ? null : `${input.temperature}°C`,
  )
  add('cargoRequirements', 'Requirements', formatRequirements(input.requirements))

  return values
}

const formatRate = (
  input: CopyLoadDetailsInput,
  settings: CopyLoadDetailsSettings,
  options: CopyLoadDetailsOptions,
): readonly string[] => {
  const rates: string[] = []

  if (has(settings, 'haulierRate') && options.canCopyHaulierRate) {
    const rate = labelled(
      'Carrier rate',
      formatMoney(options.carrierRate ?? input.carrierRate ?? input.carrier_rate),
    )
    if (rate) rates.push(rate)
  }

  if (has(settings, 'shipperRate') && options.canCopyShipperRate) {
    const rate = labelled('Customer rate', formatMoney(fullRate(input.financials)))
    if (rate) rates.push(rate)
  }

  return rates
}

export const formatLoadDetails = (
  input: CopyLoadDetailsInput,
  settings: CopyLoadDetailsSettings,
  options: CopyLoadDetailsOptions = {},
): string => {
  const output: string[] = []
  const route = formatRoute(input, settings)
  if (route) output.push(route)

  const cargo = formatCargo(input, settings)
  if (cargo.length > 0) output.push(cargo.join(settings.outputStyle === 'compact' ? ' · ' : '\n'))

  if (has(settings, 'vehicleType')) {
    const vehicle = labelled('Vehicle', formatVehicle(input))
    if (vehicle) output.push(vehicle)
  }

  output.push(...formatRate(input, settings, options))

  return output.join(settings.outputStyle === 'compact' ? ' | ' : '\n')
}

const previewCarrierRate: GetEstimatedCost = {
  amount: 120_000,
  amount_base: null,
  amount_base_decimal: null,
  amount_decimal: 1200,
  base_currency: null,
  category: null,
  created_at: new Date('2026-05-01T00:00:00.000Z'),
  currency: 'EUR',
  exchange_date: null,
  exchange_rate: null,
  id: 1,
  is_carrier_rate: true,
  is_estimated: true,
  linked_bill_id: null,
  load_id: 1,
  notes: null,
  updated_at: new Date('2026-05-01T00:00:00.000Z'),
}

const previewInput: CopyLoadDetailsInput = {
  drops: [
    {
      date: new Date('2026-05-12T00:00:00.000Z'),
      location: {
        address: {
          address: '1 Collection Street',
          city: 'Warsaw',
          country_code: 'PL',
          postcode: '00-001',
        },
        name: 'Warsaw depot',
      },
      order: 1,
      time: { from: '09:00', to: '10:00', type: 'between' },
      type: 'collection',
    },
    {
      date: new Date('2026-05-13T00:00:00.000Z'),
      order: 2,
      place: { formattedAddress: '2 Delivery Road, 30-001, Krakow, PL', name: 'Krakow consignee' },
      time: { from: '14:00', type: 'at' },
      type: 'delivery',
    },
  ],
  financials: [{ amount: 135_000, currency: { alias: 'GBP' }, type: { alias: 'rate' } }],
  goods_description: 'Palletised goods',
  load_type: { title: 'Curtainsider' },
  packaging_type: { title: 'Pallets' },
  pallets: 12,
  requirements: { adr: true, dvs: true },
  temperature: 4,
  weight: 12_000,
  weight_unit: 'kg',
}

export const formatLoadDetailsPreview = (
  settings: CopyLoadDetailsSettings,
  options: Pick<CopyLoadDetailsOptions, 'canCopyHaulierRate' | 'canCopyShipperRate'> = {},
): string =>
  formatLoadDetails(previewInput, settings, {
    canCopyHaulierRate: options.canCopyHaulierRate ?? true,
    canCopyShipperRate: options.canCopyShipperRate ?? true,
    carrierRate: previewCarrierRate,
  })
